import {ModuleWithProviders, NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterModule, Routes} from '@angular/router';
import {AccountManagementProviderService} from '@modules/account-management';
import {AuthenticationService} from '@modules/authentication';
import {ProfileService} from './profile/profile.service';
import {IsUserLogged} from '../../../guards';
import {DataEntity} from 'octopus-connect';
import {FuseSharedModule} from '@fuse/shared.module';
import {SharedModule} from 'shared/shared.module';
import {ProfileDeleteConfirmModalComponent} from './profile/profile-delete-confirm-modal/profile-delete-confirm-modal.component';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {DynamicNavigationService} from '../../../navigation/dynamic-navigation.service';
import {CommunicationCenterService} from '@modules/communication-center';
import {AccountManagementAuthorizationService} from '@modules/account-management/core/services/account-management-authorization.service';
import {UploadFileGenericComponentModule} from 'fuse-core/components/upload-file-generic/upload-file-generic.module';
import {GoBuyLicenceNewsComponent} from './onboarding/components/go-buy-licence-news/go-buy-licence-news.component';
import {GoBuyLicenceNewsPassiveComponent} from './onboarding/components/go-buy-licence-news-passive/go-buy-licence-news-passive.component';
import {FuseDirectivesModule} from 'fuse-core/directives/directives';
import {NewsletterFormComponent} from './newsletter-form/newsletter-form.component';
import {IsUserNotLogged} from '../../../guards/is-not-user-logged.class';
import {MatCardModule} from '@angular/material/card';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';


const routes: Routes = [
    {
        path: 'profile',
        canActivate: [IsUserLogged],
        loadComponent: () => import('./profile/profile.component').then(m => m.FuseProfileComponent),
        children: [
            {
                path: '',
                redirectTo: 'show-profile',
                pathMatch: 'full'
            },
            {
                path: 'show-profile',
                loadComponent: () => import('./profile/tabs/show-profile/show-profile.component').then(m => m.ShowProfileComponent),
                resolve: {
                    profile: ProfileService
                }
            },
            {
                path: 'show-profile/:mode',
                loadComponent: () => import('./profile/tabs/show-profile/show-profile.component').then(m => m.ShowProfileComponent),
                resolve: {
                    profile: ProfileService
                }
            },
            {
                path: 'link-child',
                loadComponent: () =>
                    import('./components/link-child/link-child.component')
                        .then(m => m.LinkChildComponent)
            },
            {
                path: 'unlink-parent',
                loadComponent: () =>
                    import('./components/unlink-parent/unlink-parent.component')
                        .then(m => m.UnlinkParentComponent)
            },
            {
                path: '',
                loadChildren: () => import('@modules/licensing/core/licensing.module').then(m => m.LicensingModule)
            }
        ]
    },
    {
        path: 'feedback',
        canActivate: [IsUserLogged],
        loadComponent: () =>
            import('./components/feedback/feedback.component')
                .then(m => m.FeedbackComponent)
    },
    {
        path: 'editprofile',
        canActivate: [IsUserLogged],
        loadComponent: () => import('./profile/tabs/edit-profile/forms.component').then(m => m.FuseFormsComponent),
    },
    {
        path: 'register',
        loadChildren: () => import('./register/register.module').then(m => m.RegisterModule)
    },
    {
        path     : 'validate-email/:token',
        loadChildren: () => import('@modules/account-management/core/validate-email/validate-email.module').then(m => m.ValidateEmailModule),
    }
];

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        MatProgressSpinnerModule,
        FuseSharedModule,
        SharedModule,
        RouterModule.forChild(routes),
        UploadFileGenericComponentModule,
        FuseDirectivesModule,
        MatCardModule,
        MatAutocomplete,
        MatAutocompleteTrigger
    ],
    declarations: [
        ProfileDeleteConfirmModalComponent,
        GoBuyLicenceNewsComponent,
        GoBuyLicenceNewsPassiveComponent,
        NewsletterFormComponent,
    ],
    providers: [
        IsUserNotLogged,
    ]
})
export class AccountManagementModule {
    private static isMenuSet = false;

    static forRoot(): ModuleWithProviders<AccountManagementModule> {

        return {
            ngModule: AccountManagementModule,
            providers: [
                AccountManagementProviderService,
                AuthenticationService,
                ProfileService
            ]
        };
    }

    constructor(
        private dynamicNavigation: DynamicNavigationService,
        private authService: AuthenticationService,
        private communicationCenter: CommunicationCenterService,
        private accountManagementAuthorizationService: AccountManagementAuthorizationService
    ) {
        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((data: DataEntity) => {
                if (data) {
                    this.postAuthentication();
                } else {
                    this.postLogout();
                }
            });
        this.accountManagementAuthorizationService.activeRulesOnStartup();
    }

    private postLogout(): void {
        AccountManagementModule.isMenuSet = false;
        this.dynamicNavigation.clearMenuItem('level0', 'feedback');
        this.dynamicNavigation.clearMenuItem('level0', 'unlink-parent');
        this.dynamicNavigation.clearMenuItem('level0', 'link-child');
    }

    private postAuthentication(): void {
        if (!AccountManagementModule.isMenuSet && this.authService.isAuthenticated) {
            // for connexion by gar the link is not the same than when connect by login but it s same code
            // ugly code: but not found better way
            if (!this.authService.isGAR()) {
                this.dynamicNavigation.refreshModuleMenu('level0', {
                    'id': 'feedback',
                    'title': 'feedback',
                    'translate': 'generic.feedback',
                    'type': 'link',
                    'icon': '',
                    'url': '/feedback',
                });
            }
            // for connexion by gar the link is not the same than when connect by login but it s same code
            // ugly code: but not found better way
            if (!this.authService.isGAR()) {
                this.dynamicNavigation.refreshModuleMenu('level0', {
                    'id': 'feedback',
                    'title': 'feedback',
                    'translate': 'generic.feedback',
                    'type': 'item',
                    'icon': 'feedback',
                    'url': '/feedback',
                });
            }
            // redirect dashboard'url to basic page url
            this.dynamicNavigation.registerModuleMenu('level0', {
                'id': 'unlink-parent',
                'title': 'unlink-parent',
                'translate': 'unlink_parent.title',
                'type': 'item',
                'icon': '',
                'url': '/profile/unlink-parent',
            });

            this.dynamicNavigation.registerModuleMenu('level0', {
                'id': 'link-child',
                'title': 'link-child',
                'translate': 'link_child.title',
                'type': 'item',
                'icon': 'link-child',
                'url': '/profile/link-child',
            });

            AccountManagementModule.isMenuSet = true;
        }
    }
}